import styled from "@emotion/styled";
import { PictureOutlined } from "@ant-design/icons";
import { Avatar } from "antd";
import { useNavigate } from "react-router-dom";
import { backendUrl } from "config";
import { SellerReportPeriod } from "services/interface/sellerReport.interface";
import { paths } from "setup/PageRouter/routesConfig";
import { numberFormatInt } from "utils/nuberFormat";
import { useSellerTopProducts } from "hooks/useSellerReport";
import SectionCard from "../shared/SectionCard";
import { sd } from "../tokens";

interface TopProductsCardProps {
  period: SellerReportPeriod;
}

/** API ส่ง top 5 มาเสมอ แต่การ์ดนี้โชว์แค่ 2 อันดับแรก ที่เหลือกดดูได้ที่ "ดูทั้งหมด" */
const VISIBLE_COUNT = 2;

/** API ส่ง product.image มาเป็น id ของไฟล์ ไม่ใช่ URL เต็ม (ยกเว้นกรณีที่เป็น http อยู่แล้ว) */
const toImageSrc = (imageUrl: string | null): string | undefined => {
  if (!imageUrl) return undefined;
  if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
  return `${backendUrl}images/${imageUrl}`;
};

const TopProductsCard = ({ period }: TopProductsCardProps) => {
  const navigate = useNavigate();
  const { data, isPending, isFetching, isError, refetch } = useSellerTopProducts(period);
  const products = (data ?? []).slice(0, VISIBLE_COUNT);

  return (
    <SectionCard
      eyebrow="สินค้า"
      title="สินค้าขายดี"
      minHeight={280}
      skeletonRows={4}
      loading={isPending}
      refreshing={isFetching && !isPending}
      error={isError ? true : undefined}
      onRetry={() => refetch()}
      empty={!isPending && !isError && products.length === 0}
      emptyText="ยังไม่มีสินค้าที่ขายได้ในช่วงนี้"
      extra={
        <LinkButton type="button" onClick={() => navigate(paths.manageProduct)}>
          ดูทั้งหมด →
        </LinkButton>
      }
    >
      <List>
        {products.map((product, index) => (
          <Item key={product.product_id}>
            <Rank>{index + 1}</Rank>
            <Avatar
              shape="square"
              size={40}
              src={toImageSrc(product.image_url)}
              icon={<PictureOutlined />}
              style={{ flex: "0 0 auto", background: sd.border, color: sd.subtle }}
            />
            <Info>
              <Name title={product.product_name}>{product.product_name}</Name>
              <Sub>ขายแล้ว {numberFormatInt(product.qty_sold)} ชิ้น</Sub>
            </Info>
            <Price>฿{numberFormatInt(product.total_price)}</Price>
          </Item>
        ))}
      </List>
    </SectionCard>
  );
};

export default TopProductsCard;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${sd.border};

  &:last-of-type {
    border-bottom: none;
  }
`;

const Rank = styled.span`
  width: 18px;
  flex: 0 0 auto;
  font-size: 12px;
  color: ${sd.subtle};
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${sd.ink};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${sd.subtle};
`;

const Price = styled.div`
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 700;
  color: ${sd.ink};
  white-space: nowrap;
`;

const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${sd.blue};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
